import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import {
  memberInputSchema,
  memberSchema,
  type Member,
  type MemberInput,
  type MemberProfileSnapshot,
} from '../schemas/member';

const COLLECTION = 'members';

function db() {
  return getFirebaseClients().firestore;
}

function parse(snapshotData: unknown, id: string): Member {
  const data = snapshotData as Record<string, unknown>;
  return memberSchema.parse({
    ...data,
    uid: id,
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

export async function getMember(uid: string): Promise<Member | null> {
  const snap = await getDoc(doc(db(), COLLECTION, uid));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

/**
 * Create or merge a member doc. Called once on signup, then again on each
 * sign-in to refresh `emailVerified`. Uses `setDoc(..., { merge: true })` so
 * existing profile snapshot fields aren't blown away.
 */
export async function upsertMember(input: MemberInput): Promise<void> {
  const parsed = memberInputSchema.parse(input);
  // Store email lowercased so the server-side auto-link trigger (which queries
  // members by the inscription's lowercased email) reliably finds the account.
  if (parsed.email) parsed.email = parsed.email.trim().toLowerCase();
  const now = new Date().toISOString();
  const ref = doc(db(), COLLECTION, parsed.uid);
  const existing = await getDoc(ref);
  await setDoc(
    ref,
    {
      ...parsed,
      createdAt: existing.exists() ? existing.data()?.['createdAt'] ?? now : now,
      updatedAt: now,
    },
    { merge: true },
  );
}

export async function updateMemberProfile(
  uid: string,
  profile: MemberProfileSnapshot,
): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, uid), {
    profile,
    updatedAt: serverTimestamp(),
  });
}

export async function setEmailVerified(uid: string, verified: boolean): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, uid), {
    emailVerified: verified,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Request a (re)send of the branded verification email. Writes a fresh
 * `verifyRequestedAt` timestamp to the member doc; the `onMemberWrite` Cloud
 * Function picks up the bump and sends the email via Resend. We route through a
 * doc-write (not a callable) because callable functions on this project have an
 * unreliable Cloud Run invoker binding.
 */
export async function requestEmailVerification(uid: string): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, uid), {
    verifyRequestedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
